import { Card, Grid, Typography } from '@material-ui/core'
import * as Sentry from '@sentry/react'
import { validate } from 'email-validator'
import { jwtDecode } from 'jwt-decode'
import React, { FormEvent, useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Button from '@/components/button'
import InputText from '@/components/input-text'
import { env } from '@/config/env'
import { signUp } from '@/services/sign-up'

import { useStyles } from './styles'

interface TokenPayload {
  data: {
    name: string
    email: string
  }
  exp: number
}

const Signup = () => {
  const classes = useStyles()
  const { token } = useParams<{ token: string }>()
  const [email, setEmail] = useState({ value: '', valid: false, message: '' })
  const [name, setName] = useState({ value: '', valid: true, message: '' })
  const [password, setPassword] = useState({
    value: '',
    valid: false,
    message: '',
  })
  const [confirmPassword, setConfirmPassword] = useState({
    value: '',
    valid: false,
    message: '',
  })
  const [trySignupLoading, setTrySignupLoading] = useState(false)
  const [isValidToken, setIsValidToken] = useState({
    valid: false,
    message: '',
    exp: '',
  })
  const [step, setStep] = useState(1)

  const verifyToken = useCallback(() => {
    try {
      const userData: TokenPayload = jwtDecode(token!)
      const expDate = new Date(userData.exp * 1000)
      const actualDate = new Date()
      if (expDate > actualDate) {
        setName({ value: userData.data.name ?? '', valid: true, message: '' })
        setEmail({
          value: userData.data.email ?? '',
          valid: !!userData.data.email,
          message: '',
        })
        setIsValidToken({
          valid: true,
          message: '',
          exp: expDate
            .toLocaleString([], {
              dateStyle: 'short',
              timeStyle: 'short',
            })
            .replace(' ', ' às '),
        })
      } else {
        setIsValidToken({
          valid: false,
          message: 'O token está expirado, solicite um novo!',
          exp: expDate
            .toLocaleString([], {
              dateStyle: 'short',
              timeStyle: 'short',
            })
            .replace(' ', ' às '),
        })
      }
    } catch (e) {
      Sentry.captureException(e)
      setIsValidToken({
        valid: false,
        message: 'Token inválido!',
        exp: '',
      })
    }
  }, [])

  const handleEmail = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value

    if (newEmail && validate(newEmail)) {
      setEmail({ value: newEmail, valid: true, message: '' })
    } else if (newEmail) {
      setEmail({
        value: newEmail,
        valid: false,
        message: 'O email informado é inválido!',
      })
    } else {
      setEmail({ value: newEmail, valid: false, message: '' })
    }
  }, [])

  const handleName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value

    if (newName && newName.length > 20) {
      setName({
        value: newName,

        valid: false,
        message: 'É preciso ter no máximo 20 caracteres',
      })
    } else {
      setName({
        value: newName,
        valid: true,
        message: '',
      })
    }
  }, [])

  const handlePassword = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newPassword = e.target.value

      if (newPassword) {
        if (newPassword.length > 20) {
          setPassword({
            value: newPassword,
            valid: false,
            message: 'É preciso ter no máximo 20 caracteres',
          })
        } else if (newPassword.length < 8) {
          setPassword({
            value: newPassword,
            valid: false,
            message: 'É preciso ter no mínimo 8 caracteres',
          })
        } else {
          setPassword({ value: newPassword, valid: true, message: '' })
        }
      } else {
        setPassword({ value: newPassword, valid: false, message: '' })
      }
    },
    [],
  )

  const submitSignup = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()
      setTrySignupLoading(true)

      const setError = () => {
        setPassword({
          value: password.value,
          valid: false,
          message: 'Houve um erro ao enviar o cadastro',
        })
        setConfirmPassword({
          value: confirmPassword.value,
          valid: false,
          message: '',
        })
        setTrySignupLoading(false)
      }

      try {
        const response = await signUp(
          { email: email.value, password: password.value, name: name.value },
          token!,
        )
        if (response.status === 200) {
          setStep(3)
        } else {
          setError()
        }
      } catch (error) {
        setError()
      }
    },
    [email.value, name.value, password.value, confirmPassword.value],
  )

  useEffect(() => {
    if (password.value !== confirmPassword.value && confirmPassword.value) {
      setConfirmPassword({
        value: confirmPassword.value,
        valid: false,
        message: 'Senhas não coincidem!',
      })
    } else if (confirmPassword.value) {
      setConfirmPassword({
        value: confirmPassword.value,
        valid: true,
        message: '',
      })
    } else {
      setConfirmPassword({
        value: confirmPassword.value,
        valid: false,
        message: '',
      })
    }
  }, [password.value, confirmPassword.value])

  useEffect(() => {
    verifyToken()
  }, [])

  return (
    <Grid
      container
      alignItems='center'
      justifyContent='center'
      className={classes.main}
    >
      <Card className={classes.content}>
        <img src='assets/img/atlas-logo.svg' alt='Logo do Cromai Atlas' />
        <form
          data-testid='signup-form'
          onSubmit={submitSignup}
          className={classes.content}
        >
          <Typography variant='h1'>Cromai Atlas</Typography>
          <Typography variant={'caption'} className={classes.swVersion}>
            {env.softwareVersion}
          </Typography>
          {isValidToken.valid ? (
            <>
              <Typography variant='body1'>
                {step !== 3 ? 'Cadastro da conta Cromai' : 'Cadastro concluído'}
              </Typography>
              {step === 1 && (
                <>
                  <InputText
                    label='E-mail'
                    id='email'
                    value={email.value}
                    onChange={handleEmail}
                    error={Boolean(email.message && !email.valid)}
                    helperText={email.message}
                  />

                  <InputText
                    label='Nome'
                    id='name'
                    value={name.value}
                    onChange={handleName}
                    error={!name.valid}
                    helperText={name.message}
                  />
                  <Button
                    className={classes.button}
                    type='button'
                    onClick={() => setStep(2)}
                    loading={trySignupLoading}
                    disabled={!email.valid || !name.valid}
                  >
                    Próximo
                  </Button>
                </>
              )}
              {step === 2 && (
                <>
                  <InputText
                    id='password'
                    value={password.value}
                    onChange={handlePassword}
                    label={'Nova senha'}
                    type={'password'}
                    success={password.message === '' && password.valid}
                    error={password.message !== ''}
                    helperText={password.message}
                  />
                  <InputText
                    id='confirmPassword'
                    value={confirmPassword.value}
                    onChange={(e) =>
                      setConfirmPassword((state) => ({
                        ...state,
                        value: e.target.value,
                      }))
                    }
                    label={'Confirme a nova senha'}
                    type={'password'}
                    success={
                      confirmPassword.message === '' && confirmPassword.valid
                    }
                    error={confirmPassword.message !== ''}
                    helperText={confirmPassword.message}
                  />
                  <Button
                    className={classes.submitButton}
                    type='submit'
                    disabled={!password.valid || !confirmPassword.valid}
                    loading={trySignupLoading}
                  >
                    Criar conta
                  </Button>
                </>
              )}
              {step === 3 && (
                <Typography className={classes.finished} variant={'body1'}>
                  Confirme a criação da conta até{' '}
                  <strong>{isValidToken.exp}</strong> a partir de um email
                  enviado para: <strong>{email.value}</strong>
                </Typography>
              )}
            </>
          ) : isValidToken.exp ? (
            <Typography variant={'body1'} color='error'>
              O link expirou em <strong>{isValidToken.exp}</strong>.
            </Typography>
          ) : (
            <Typography variant={'body1'} color='error'>
              Não foi possível validar o link.
            </Typography>
          )}
        </form>
      </Card>
    </Grid>
  )
}

export default Signup
